import React, { useRef } from 'react';
import Axios from 'axios';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { TCITooltip } from '@/components/ui/Tooltip';
import { SpinnerIcon } from '@/components/common/Utils';
import * as Routes from '@/modules/routes';
import playButton from 'images/icons/play.svg';
import pauseButton from 'images/icons/pause.svg';
import cn from '@/utils/cn';
import { currentSelectionRange, restoreSelectionRange } from '@/utils/selection';
import styles from './TextToSpeechButton.module.scss';
import useSpeechstream from './useSpeechStream';
import SpeakElementButton from './SpeakElementButton';
import './speechstream.scss';
import SpeedSelectMenu from './SpeedSelectMenu';
import useSpeechSpeed, { TTS_SPEED_QUERY_KEY } from './useSpeechSpeed';

interface TTSButtonProps {
  idToRead?: string;
  isInDraggableToolbar?: boolean;
  locale: Locale;
  page?: number;
}

const TextToSpeechButton = ({ idToRead, isInDraggableToolbar, locale, page }: TTSButtonProps) => {
  const setSpeedRoute = Routes.plato_api_settings_path();
  const savedSelectionRef = useRef(null);
  const queryClient = useQueryClient();
  const { data: selectedSpeed } = useSpeechSpeed();
  const config = { idToRead, locale, page, speed: selectedSpeed };
  const { canUseUpdatedSpeechstream, playing, toggleSpeech, stop, loaded } = useSpeechstream(config);

  if (idToRead) {
    return (
      <SpeakElementButton
        idToRead={idToRead}
        locale={locale}
        speed={selectedSpeed}
      />
    );
  }

  const label = playing ? 'Pause' : 'Play';
  const iconSrc = playing ? pauseButton : playButton;
  const style = (!isInDraggableToolbar && playing) ? { backgroundColor: '#522e91' } : {};
  const playButtonClass = () => {
    const buttonStyle = (!canUseUpdatedSpeechstream || locale.toLowerCase() === 'es')
      ? styles.textAction
      : styles.ttsActionPlay;

    if (loaded) return buttonStyle;

    return `${buttonStyle} ${styles.loading}`;
  };

  const saveUserPreferredSpeed = (newSpeed: string) => {
    if (newSpeed === selectedSpeed) return;

    Axios.patch(setSpeedRoute, { settings: { text_to_speech_speed: newSpeed } })
      .then(() => queryClient.setQueryData(TTS_SPEED_QUERY_KEY, newSpeed));
  };

  // AIDEV-NOTE: On touch devices, tapping Play collapses the page selection before the click reads
  // it, so play() falls back to reading the whole element instead of the highlight. Capture the
  // range on pointer-down (before the tap clears it) and restore it just before playing.
  const captureSelection = () => {
    savedSelectionRef.current = currentSelectionRange();
  };

  const playSelectionOrElement = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length === 0) {
      restoreSelectionRange(savedSelectionRef.current);
    }

    toggleSpeech();
  };

  return (
    <div className="tw-flex">
      <TCITooltip>
        <TCITooltip.Trigger asChild>
          <button
            aria-label={label}
            className={cn(playButtonClass(), 'tw-h-10', {
              '!tw-h-8 !tw-w-7 !tw-p-0 !tw-mr-0 hover:!tw-bg-white/30': isInDraggableToolbar,
            })}
            style={style}
            type="button"
            onClick={playSelectionOrElement}
            onPointerDown={captureSelection}
          >
            {loaded ? (
              <img
                alt=""
                className={cn('standard', { 'tw-brightness-0 tw-invert': isInDraggableToolbar })}
                src={iconSrc}
              />
            ) : <SpinnerIcon />}
          </button>
        </TCITooltip.Trigger>
        <TCITooltip.Content>{label}</TCITooltip.Content>
      </TCITooltip>
      {(canUseUpdatedSpeechstream && locale.toLowerCase() === 'en') && (
        <SpeedSelectMenu
          draggableToolbarClass={isInDraggableToolbar ? 'hover:!tw-bg-white/30 !tw-mr-[2px]' : ''}
          selectedSpeed={selectedSpeed}
          setSelectedSpeed={saveUserPreferredSpeed}
        />
      )}

      <TCITooltip>
        <TCITooltip.Trigger asChild>
          <button
            aria-label="Stop"
            className={cn(styles.textAction, styles.buttonSize,
              { '!tw-h-8 !tw-w-8 !tw-p-0 tw-flex tw-items-center tw-justify-center tw-mr-0': isInDraggableToolbar,
                'hover:!tw-bg-white/30': isInDraggableToolbar,
                'tw-h-10': !isInDraggableToolbar })}
            type="button"
            onClick={stop}
          >
            {loaded ? <i className="fa fa-solid fa-stop tw-text-tci-purple" /> : <SpinnerIcon />}
          </button>
        </TCITooltip.Trigger>
        <TCITooltip.Content>Stop</TCITooltip.Content>
      </TCITooltip>
    </div>
  );
};

const queryClient = new QueryClient();
const TextToSpeechButtonWrapper = ({ idToRead, isInDraggableToolbar, locale, page }: TTSButtonProps) => (
  <QueryClientProvider client={queryClient}>
    <TextToSpeechButton
      idToRead={idToRead}
      isInDraggableToolbar={isInDraggableToolbar}
      locale={locale}
      page={page}
    />
  </QueryClientProvider>
);

export default TextToSpeechButtonWrapper;
